import dayjs from 'dayjs';
import React, {
  forwardRef,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import TableChartIcon from '@material-ui/icons/TableChart';
import { usePrompts } from '@eas/common-web';
import { AllDayRouteMenuItem } from '@modules/plan/work-order-menu/menu-items/all-day-route';
import { useWorkOrderDetails } from '@modules/planner/work-order-menu/work-order-details';
import { Multitask } from '@composite/multitask/types';
import { useWorkOrderExceptions } from '@composite/planner/work-order-exceptions';
import { LockWorkOrderMenuItem } from '@composite/work-order/context-menu/lock-work-order-menu-item';
import { UnplanWorkOrderMenuItem } from '@composite/work-order/context-menu/unplan-work-order-menu-item';
import { WorkOrder } from '@models';
import { BulkOperationsContext } from '../calendar/controls/bulk-operations/bulk-operations-context';
import { PlannerContext } from '../planner-context';
import {
  getMultitaskBusinessWorkOrderIds,
  getMultitaskInternalWorkOrderIds,
  getSelectedBusinessWorkOrderIds,
  getSelectedInternalWorkOrderIds,
  getWorkOrderBusinessId,
  getWorkOrderInternalId,
  replaceBulkSelectionWithMultitaskRefs,
} from '../work-order-id-utils';
import { WorkOrderBulkOperationsMenuItems } from './menu-items/bulk-operations/show-in-workorder-table-item';
import { DetailsMenuItem } from './menu-items/details';
import { ErrorsMenuItem } from './menu-items/errors';
import { ExpectedMaterialMenuItem } from './menu-items/expected-material';
import { OpenMenuItem } from './menu-items/open';
import { ResyncWoMenuItem } from './menu-items/resync';
import { RouteMenuItem, getUnitStartingPoint } from './menu-items/route';
import { ShowInCalendar } from './menu-items/show-in-calendar';
import { StateMenuItem } from './menu-items/state';
import { useTravelTo } from './work-order-travel-to';

type WorkOrderMenuProps = {
  previousWorkOrder?: WorkOrder;
  nextWorkOrder?: WorkOrder;
  workOrder: WorkOrder;
  stickToPointer?: boolean;
  options: {
    allDayRouteOption?: boolean;
    showInCalendarOption?: boolean;
    showInWorkOrderTableOption?: boolean;
  };
};

export type WorkOrderMenuHandle = {
  open: (event: React.MouseEvent<HTMLElement>) => void;
};

type MultitaskWorkOrderMenuProps = {
  multitask: Multitask;
  stickToPointer?: boolean;
};

const ShowWorkOrderInTableMenuItem = ({
  closeMenu,
  workOrder,
}: {
  closeMenu: () => void;
  workOrder: WorkOrder;
}) => {
  const { showSelectedWorkOrdersInTable } = useContext(PlannerContext);

  return (
    <MenuItem
      onClick={() => {
        const workOrderId = getWorkOrderBusinessId(workOrder);
        const id = getWorkOrderInternalId(workOrder);

        if (!workOrderId || !id) {
          closeMenu();
          return;
        }

        showSelectedWorkOrdersInTable({
          ids: [id],
          workOrderIds: [workOrderId],
        });
        closeMenu();
      }}
    >
      <ListItemIcon>
        <TableChartIcon fontSize="small" />
      </ListItemIcon>
      Zobraz v zozname PP
    </MenuItem>
  );
};

export const WorkOrderMenu = forwardRef(function WorkOrderMenu(
  {
    previousWorkOrder,
    nextWorkOrder,
    workOrder,
    stickToPointer,
    options,
  }: WorkOrderMenuProps,
  ref: React.Ref<WorkOrderMenuHandle>
) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeMenu = () => setAnchorEl(null);

  const [left, setLeft] = useState(0);

  const { calendarSource, workOrderTableRef } = useContext(PlannerContext);
  const { bulkEditModeOn } = useContext(BulkOperationsContext);

  useImperativeHandle(ref, () => ({
    open: (event: React.MouseEvent<HTMLElement>) => {
      if (stickToPointer) {
        setLeft(event.nativeEvent.clientX);
      }
      setAnchorEl(event.currentTarget);
    },
  }));

  useWorkOrderDetails({
    id: workOrder.id,
    showConfirm: false,
  });

  useWorkOrderExceptions({
    id: workOrder.id,
    exceptions: workOrder.validationErrors ?? [],
    showConfirm: false,
    showText: false,
  });

  useTravelTo({
    id: workOrder.id,
    workOrders: [previousWorkOrder, workOrder],
    unitStartingPoint: getUnitStartingPoint(workOrder),
    addStartingPoint: previousWorkOrder === undefined,
    addReturnPoint: nextWorkOrder === undefined,
  });

  usePrompts([
    {
      key: `CHANGE_STATE_ALERT_${workOrder.id}`,
      dialogTitle: 'Manuálna zmena stavu',
      dialogShowClose: true,
      dialogShowConfirm: true,
      dialogWidth: 400,
      dialogText: 'Naozaj chcete zmeniť stav PP?',
    },
  ]);

  usePrompts(
    [
      {
        key: `WORK_ORDER_LOCK_ALERT_${workOrder.id}`,
        dialogTitle: 'Zamknúť / odomknúť pracovný príkaz',
        dialogShowClose: true,
        dialogShowConfirm: true,
        dialogWidth: 400,
        dialogText: (
          <div>
            Naozaj chcete <b>{workOrder.locked ? 'odomknúť' : 'zamknúť'}</b>{' '}
            pracovný príkaz?
          </div>
        ),
      },
    ],
    [workOrder.locked]
  );

  usePrompts(
    [
      {
        key: `WORK_ORDER_UNPLAN_ALERT`,
        dialogTitle: 'Odplánovať pracovný príkaz',
        dialogShowClose: true,
        dialogShowConfirm: true,
        dialogWidth: 400,
        dialogText: (
          <div>
            Naozaj chcete <b>odplánovať</b> pracovný príkaz?
          </div>
        ),
      },
    ],
    []
  );

  if (bulkEditModeOn) {
    return (
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'top',
          horizontal: left === 0 ? 'right' : left,
        }}
      >
        <WorkOrderBulkOperationsMenuItems
          closeMenu={closeMenu}
          // workOrder={workOrder}
          // previousWorkOrder={previousWorkOrder}
        />
      </Menu>
    );
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={() => {
        setAnchorEl(null);
      }}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: 'top',
        horizontal: left === 0 ? 'right' : left,
      }}
    >
      <DetailsMenuItem closeMenu={closeMenu} workOrder={workOrder} />
      <OpenMenuItem closeMenu={closeMenu} workOrder={workOrder} />
      {options.showInWorkOrderTableOption && (
        <ShowWorkOrderInTableMenuItem
          closeMenu={closeMenu}
          workOrder={workOrder}
        />
      )}
      <ErrorsMenuItem closeMenu={closeMenu} workOrder={workOrder} />
      {options.showInCalendarOption &&
        workOrder.currentState?.code &&
        workOrder.currentState.code !== 'NOV' &&
        workOrder.currentState.code !== 'ZRU' && (
          <ShowInCalendar closeMenu={closeMenu} workOrder={workOrder} />
        )}
      <RouteMenuItem
        closeMenu={closeMenu}
        workOrder={workOrder}
        previousWorkOrder={previousWorkOrder}
      />
      {options.allDayRouteOption && workOrder.startTime && (
        <AllDayRouteMenuItem
          closeMenu={closeMenu}
          date={dayjs(workOrder.startTime).format('YYYY-MM-DD')}
          unitId={workOrder.user?.id ?? workOrder.mobileUnit?.id ?? ''}
          startingPoint={getUnitStartingPoint(workOrder)}
        />
      )}
      <LockWorkOrderMenuItem
        closeMenu={closeMenu}
        workOrder={workOrder}
        afterSubmit={() => {
          calendarSource?.yAxis.refreshData();
          workOrderTableRef.current?.refresh();
        }}
      />
      <UnplanWorkOrderMenuItem
        closeMenu={closeMenu}
        workOrder={workOrder}
        afterSubmit={() => {
          calendarSource?.yAxis.refreshData();
          workOrderTableRef.current?.refresh();
        }}
      />
      <ResyncWoMenuItem closeMenu={closeMenu} workOrder={workOrder} />
      <ExpectedMaterialMenuItem
        closeMenu={closeMenu}
        workOrder={workOrder}
        afterSubmit={() => {
          calendarSource?.yAxis.refreshData();
          workOrderTableRef.current?.refresh();
        }}
      />
      <StateMenuItem closeMenu={closeMenu} workOrder={workOrder} />
    </Menu>
  );
});

export const MultitaskWorkOrderMenu = forwardRef(
  function MultitaskWorkOrderMenu(
    { multitask, stickToPointer }: MultitaskWorkOrderMenuProps,
    ref: React.Ref<WorkOrderMenuHandle>
  ) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [left, setLeft] = useState(0);
    const closeMenu = () => setAnchorEl(null);

    const { showSelectedWorkOrdersInTable } = useContext(PlannerContext);
    const { bulkEditModeOn, selected, replaceSelection } = useContext(
      BulkOperationsContext
    );

    useImperativeHandle(ref, () => ({
      open: (event: React.MouseEvent<HTMLElement>) => {
        if (stickToPointer) {
          setLeft(event.nativeEvent.clientX);
        }
        setAnchorEl(event.currentTarget);
      },
    }));

    return (
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'top',
          horizontal: left === 0 ? 'right' : left,
        }}
      >
        <MenuItem
          onClick={() => {
            if (bulkEditModeOn) {
              const multitaskKey = `MULTITASK_${multitask.day}_${multitask.unitId}`;
              const nextSelected = replaceBulkSelectionWithMultitaskRefs(
                selected,
                multitaskKey,
                multitask.workOrderRefs,
                multitask.unitId
              );
              const workOrderIds =
                getSelectedBusinessWorkOrderIds(nextSelected);
              const ids = getSelectedInternalWorkOrderIds(nextSelected);

              if (workOrderIds.length === 0 || ids.length === 0) {
                closeMenu();
                return;
              }

              replaceSelection(nextSelected);
              showSelectedWorkOrdersInTable({ ids, workOrderIds });
              closeMenu();
              return;
            }

            const workOrderIds = getMultitaskBusinessWorkOrderIds(multitask);
            const ids = getMultitaskInternalWorkOrderIds(multitask);

            if (workOrderIds.length === 0 || ids.length === 0) {
              closeMenu();
              return;
            }

            showSelectedWorkOrdersInTable({ ids, workOrderIds });
            closeMenu();
          }}
        >
          <ListItemIcon>
            <TableChartIcon fontSize="small" />
          </ListItemIcon>
          Zobraz označené v zozname PP
        </MenuItem>
      </Menu>
    );
  }
);
